import { ApiError } from '../../lib/apiFetch.js';

import type { TimesheetEntryRow, TimesheetGridRow } from './contractsMirror.js';
import { useMyWeekQuery } from './useMyWeekQuery.js';
import { useTimesheetsQuery } from './useTimesheetsQuery.js';

/**
 * The signed-in person's own week, from the two endpoints that answer for it, joined here so no
 * screen has to join them twice and get a different answer.
 *
 * ⚠ `rows[0]` IS NOT YOU. `GET /api/v1/timesheets/week-grid` scopes itself to one employee ONLY
 * when `user.role === 'Employee'`; for every other role it answers with the whole tenant, ordered
 * by name (`loadAdminWeeklyHours`, `ORDER BY e.name ASC`). Home picks the crew layout by
 * CAPABILITY, not by role — a Manager stripped of `reports.view` and `jobs.view` lands on it — so
 * reading `rows[0]` puts the alphabetically first employee's hours under the heading "My week".
 * The caller's own row is the one matching `scope.employeeId`, which only the punch-list response
 * carries; the session has no employee id on it.
 *
 * WHY THE GRID AND NOT THE PUNCH-LIST SUMMARY. Both are server-computed and lunch-adjusted, but the
 * grid is what the timesheet screen shows and what the office approves. Two figures on one screen
 * from two endpoints are free to disagree, and nothing would have said so.
 *
 * Both queries are the ones the screens already ran, under their existing keys, so mounting this in
 * two cards on the same Home costs one response each rather than two.
 */
export interface MyWeek {
  /** This person's individual punches for the week. */
  entries: TimesheetEntryRow[];
  /** This person's OWN row in the week grid, or `null` when the grid has not answered for them. */
  row: TimesheetGridRow | null;
  /** The 7 ISO dates of the week — the column keys for `row.days`. */
  dates: string[];
  /**
   * Edit requests waiting on a supervisor FOR THIS PERSON. The queue on the response is the whole
   * tenant's for anybody who is not an `Employee`, exactly like `rows`, so it is filtered here too.
   */
  pendingEdits: number;
  /** The grid has not answered yet — render nothing rather than a zero. */
  isPending: boolean;
  /**
   * The week cannot be answered: the grid request failed, OR the punch list failed. The row is a
   * JOIN of the two, so a failed punch list is a failed week even when the grid answered. Without
   * this, a failed identity lookup printed an em dash and "Not approved yet" with no error and no
   * retry. (`employee_required` is not a failure, see `entriesFailed`.)
   */
  isError: boolean;
  /** The punch list is still loading. */
  entriesPending: boolean;
  /**
   * The punch list FAILED. A 400 `employee_required` is not a failure: it is an office login with
   * no employee record, which is a real empty week and reads as one.
   */
  entriesFailed: boolean;
  /** Refetch both. One press should heal one blip, whichever half of it broke. */
  refetch: () => void;
}

export function useMyWeek(enabled: boolean): MyWeek {
  const week = useMyWeekQuery(enabled);
  const grid = useTimesheetsQuery({});

  const myEmployeeId = week.data?.scope?.employeeId ?? null;
  const row =
    myEmployeeId === null
      ? null
      : (grid.data?.rows?.find((candidate) => candidate.employeeId === myEmployeeId) ?? null);

  const entriesFailed =
    week.isError && !(week.error instanceof ApiError && week.error.message === 'employee_required');

  return {
    entries: week.data?.timesheets ?? [],
    row,
    dates: grid.data?.dates ?? [],
    pendingEdits:
      myEmployeeId === null
        ? 0
        : (grid.data?.pendingRequests?.filter((r) => r.employeeId === myEmployeeId).length ?? 0),
    isPending: grid.isPending || week.isPending,
    isError: grid.isError || entriesFailed,
    entriesPending: week.isPending,
    entriesFailed,
    refetch: () => {
      void week.refetch();
      void grid.refetch();
    },
  };
}
